import { Fragment } from 'react/jsx-runtime';
import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';

import { Icon } from '../../shared/Icon';

import { usePortalTags } from '../../hooks/portal';

import { toHTMLDateWithoutTime } from '../../utils/dates';

const WEEKDAYS_OPTIONS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

interface Rule {
    tag: string;
    weekday: string;
}
export interface AutomaticCategoryInput {
    startDate?: Date;
    onStartDateChange: (startDate: Date) => void;
    endDate?: Date;
    onEndDateChange: (endDate: Date) => void;
    rules: Array<Rule>;
    onRuleAdded: (rule: Rule) => void;
    onRuleRemoved: (rule: Rule) => void;
    onRuleChanged: (oldRule: Rule, newRule: Rule) => void;
}
export const AutomaticCategoryInput = ({
    startDate,
    onStartDateChange,
    endDate,
    onEndDateChange,
    rules,
    onRuleAdded,
    onRuleRemoved,
    onRuleChanged,
}: AutomaticCategoryInput) => {
    const { data: availableTags = [] } = usePortalTags();

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Automatic Scheduling Rules
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                <TextField
                    value={toHTMLDateWithoutTime(startDate)}
                    label="Start date"
                    size="small"
                    type="date"
                    onChange={(e) =>
                        onStartDateChange(new Date(e.target.value))
                    }
                    slotProps={{
                        inputLabel: { shrink: true },
                    }}
                />
                <TextField
                    value={toHTMLDateWithoutTime(endDate)}
                    size="small"
                    label="End date"
                    type="date"
                    onChange={(e) => onEndDateChange(new Date(e.target.value))}
                    slotProps={{
                        inputLabel: { shrink: true },
                    }}
                />
            </Box>
            <Box
                sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}
            ></Box>
            <Box sx={{ mt: 2 }}>
                {rules.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No rules added yet.
                    </Typography>
                )}
                <Grid container spacing={1}>
                    {rules.map((rule, index) => (
                        <Fragment key={index}>
                            <Grid size={5}>
                                <Autocomplete
                                    size="small"
                                    options={availableTags.map(
                                        (tag) => tag.name,
                                    )}
                                    value={rule.tag}
                                    onChange={(_, value) =>
                                        onRuleChanged(rule, {
                                            ...rule,
                                            tag: value || '',
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Tag" />
                                    )}
                                />
                            </Grid>
                            <Grid size={5}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel id="demo-simple-select-label">
                                        Weekday
                                    </InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        value={rule.weekday}
                                        label="Age"
                                        onChange={(e) => {
                                            onRuleChanged(rule, {
                                                ...rule,
                                                weekday: e.target.value || '',
                                            });
                                        }}
                                    >
                                        {WEEKDAYS_OPTIONS.map((weekday) => (
                                            <MenuItem
                                                key={weekday}
                                                value={weekday}
                                            >
                                                {weekday}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={2}>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onRuleRemoved(rule)}
                                >
                                    <Icon name="delete" />
                                </IconButton>
                            </Grid>
                        </Fragment>
                    ))}
                    <Grid size={12}>
                        <Button
                            variant="contained"
                            onClick={() =>
                                onRuleAdded({
                                    tag: '',
                                    weekday: '',
                                })
                            }
                            startIcon={<Icon name="add" />}
                        >
                            Add Rule
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};
